// Depo Haritası Editörü (Lokasyon Yönetimi › 2D Görsel Harita) kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'wms.mapEditor';

export const wmsMapEditorGuide = (t: TFunction): GuideContent => ({
  layout: 'card',
  title: t(`${G}.title`, 'Depo haritası nasıl çizilir?'),
  intro: t(`${G}.intro`, 'Deponun kuşbakışı planını çizersiniz: bölge, koridor, peron ve raflar. Çizdiğiniz yerleşim toplama sırasını ve toplayıcının rotasını belirler. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    header: t(`${G}.regions.header`, 'Araç çubuğu'),
    nav: t(`${G}.regions.nav`, 'Özellikler paneli'),
    content: t(`${G}.regions.content`, 'Harita'),
    footer: t(`${G}.regions.footer`, 'Durum çubuğu'),
  },
  navItems: [
    t(`${G}.nav.warehouseSettings`, 'Depo Ayarları'),
    t(`${G}.nav.selected`, 'Seçili nesne'),
    t(`${G}.nav.occupancy`, 'Depo Doluluk Oranı'),
  ],
  steps: [
    {
      region: 'nav',
      title: t(`${G}.s1.title`, '1. Depo alanını ayarlayın'),
      text: t(`${G}.s1.text`, 'Hiçbir nesne seçili değilken soldaki panelde "Depo Ayarları" görünür. Genişlik (grid) ve Yükseklik (grid) ile çizim alanının boyutunu girin; 1 grid yaklaşık 1 metredir. Panelin altında Depo Doluluk Oranı (dolu / toplam lokasyon) görünür.'),
      tips: [
        t(`${G}.s1.tip1`, 'Depo boyutu bu tarayıcıda saklanır; başka bir bilgisayarda yeniden ayarlamanız gerekebilir.'),
        t(`${G}.s1.tip2`, 'Araç çubuğunun başındaki düğme yan paneli açıp kapatır.'),
      ],
    },
    {
      region: 'header',
      title: t(`${G}.s2.title`, '2. Bölge, koridor ve peron çizin'),
      text: t(`${G}.s2.text`, 'Araç çubuğundan bir çizim aracı seçip haritada sürükleyin. Sıra şöyledir: önce "Bölge Ekle", sonra bölgenin içine "Koridor Ekle", sonra koridorun yanına "Peron Ekle".'),
      tips: [
        t(`${G}.s2.tip1`, 'Koridor bir bölgenin içinde olmalıdır; bölge ve koridor başka bir nesnenin üstüne çizilemez.'),
        t(`${G}.s2.tip2`, 'Peron için yakında bir koridor olmalıdır. Peron koridora yapışık olmalı, koridorun ya da başka bir peronun üstüne çizilemez.'),
      ],
    },
    {
      region: 'content',
      title: t(`${G}.s3.title`, '3. Raf ekleyin'),
      text: t(`${G}.s3.text`, '"Raf Ekle" ile bir peronun içine tıklayarak tek raf koyun. "Toplu Raf" ile peronun içine tıklayınca pencere açılır: Raf Sayısı ve "Dikey raf seviyesi (üst üste)" (1–6) girip "Oluştur"a basın. Üst üste raflar Ağaç Görünümü\'nde alt dal olarak görünür.'),
      tips: [
        t(`${G}.s3.tip1`, 'Raf renkleri doluluğu gösterir: yeşil boş, sarı kısmen dolu, turuncu dolu.'),
        t(`${G}.s3.tip2`, 'Seç / Taşı aracındayken bir rafa tıklamak raftaki ürünleri (ürün, barkod, miktar) gösterir.'),
      ],
    },
    {
      region: 'header',
      title: t(`${G}.s4.title`, '4. Giriş/çıkış ve engelleri işaretleyin'),
      text: t(`${G}.s4.text`, '"Giriş/Çıkış" aracıyla deponun giriş ve çıkış noktalarını koyun; toplama sırası giriş noktasından başlayarak hesaplanır. "Engel" aracıyla duvar, sütun, yangın çıkışı gibi geçilemeyen yerleri çizin; tipini soldaki panelde seçin.'),
      tips: [
        t(`${G}.s4.tip1`, 'Engel koridorun üstüne konabilir; peron, raf veya giriş/çıkışın üstüne konamaz. Engeller toplama sırasına girmez.'),
      ],
    },
    {
      region: 'nav',
      title: t(`${G}.s5.title`, '5. Seçin, taşıyın, düzenleyin'),
      text: t(`${G}.s5.text`, '"Seç / Taşı" aracında bir nesneye tıklayın: sürükleyerek taşıyın, kenarından boyutlandırın. Soldaki panelde ad, ölçü ve bağlı koridor / peron gibi özellikleri değiştirin. "Çoğalt" seçili nesnenin aynı ölçülerde kopyasını ekler, "Geri Al" son değişikliği geri alır.'),
      tips: [
        t(`${G}.s5.tip1`, 'Bölge, koridor veya peron silinirse içindeki ve ona bağlı öğeler de birlikte silinir; kaç öğenin silindiği bildirilir.'),
        t(`${G}.s5.tip2`, 'Fare tekerleği yakınlaştırır; sağ tık ile ya da boşluk tuşunu basılı tutarak sürüklemek haritayı kaydırır. "Ekrana Sığdır" tüm depoyu ekrana getirir.'),
      ],
    },
    {
      region: 'footer',
      title: t(`${G}.s6.title`, '6. Kaydedin'),
      text: t(`${G}.s6.text`, 'Değişiklikler siz "Kaydet"e basana kadar yalnız ekrandadır; durum çubuğunda "● Kaydedilmemiş değişiklikler" yazar. Kaydedince bütün değişiklikler tek seferde kaydedilir ve toplayıcının yürüme yolu haritası arka planda yenilenir. Durum çubuğu bölge, koridor, peron, raf ve giriş/çıkış sayılarını gösterir.'),
    },
    {
      region: 'header',
      title: t(`${G}.s7.title`, '7. Toplama sırası ve rota önizleme'),
      text: t(`${G}.s7.text`, '"SortOrder" düğmesi raflara toplama sıra numarası verir; bunun için haritada bir giriş noktası olmalıdır. Hesap kaydedilmiş haritaya göre yapılır, bu yüzden önce kaydedin. "SO" katmanı numaraları haritada gösterir.'),
      tips: [
        t(`${G}.s7.tip1`, 'Rota önizleme: "Siparişler" düğmesini açıp haritada raflara tıklayarak örnek bir sipariş seçin, sonra "Rota"ya basın. Optimal (en kısa), Soldan başla, Sağdan başla seçenekleri arasında geçiş yapabilir, ileri ok ile durak durak ilerleyebilirsiniz. Önizleme yalnız gösterimdir, kayıt değiştirmez.'),
        t(`${G}.s7.tip2`, 'Rota önizleme için önce SortOrder hesaplanmış olmalıdır.'),
        t(`${G}.s7.tip3`, 'Katman düğmeleri (SO, Rota, Engel, etiketler, grid) yalnız görünümü değiştirir. Tam ekran düğmesi haritayı büyütür.'),
      ],
    },
  ],
});
